import React, { useState } from "react";
import { CreateTimeoffRuleRequestType } from "../../../types/request/timeoff";
import {
    TimeOffAccrualPeriodEnum,
    TimeoffAccrualWaitingPeriod,
    TimeoffApprovalStatusEnum,
    TimeoffCarryOverRule,
} from "../../../enums";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { CatchingPokemonSharp } from "@mui/icons-material";
import { DateTimeUtil } from "../../../utils";
import {
    Milestone,
    TimeoffAccrualTitle,
    TimeoffAccrualWaitingPeriodTitle,
    TimeoffCarryOverRuleTitle,
} from "../../../types";
import { PlusCircle } from "lucide-react";
import { MilestoneForm } from "../../../components/timeOff";

export type AccrualSetupStepProps = {
    formData: CreateTimeoffRuleRequestType;
    setFormData: React.Dispatch<React.SetStateAction<CreateTimeoffRuleRequestType>>;
};

const AccrualSetupStep = ({ formData, setFormData }: AccrualSetupStepProps) => {
    const handleChange = (field: string, value: any) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const [showOtherDatePicker, setShowOtherDatePicker] = useState(false);
    const [carryOverRulesSelectedvalue, setCarryOverRulesSelectedvalue] = useState("");

    const user = useSelector((state: RootState) => state.user.user);

    const [milestones, setMilestones] = useState<Milestone[]>([]);

    const [selectedWaitingPeriodType, setSelectedWaitingPeriodType] = useState<TimeoffAccrualWaitingPeriod>(
        TimeoffAccrualWaitingPeriod.DAYS
    );

    const removeMilestone = (order: number) => {
        setMilestones(milestones.filter((m) => m.order !== order));
    };

    const updateMilestone = (updatedMilestone: Milestone) => {
        setMilestones((prev) => prev.map((m) => (m.order === updatedMilestone.order ? updatedMilestone : m)));
        setFormData((prev) => ({
            ...prev,
            milestones: milestones.map((m) => (m.order === updatedMilestone.order ? updatedMilestone : m)),
        }));
    };

    const addMilestone = () => {
        setMilestones((prev) => [
            ...prev,
            {
                order: prev.length + 1,
            },
        ]);
    };

    const inspect = () => {
    };

    const ordinalNumbers = [
        "1st",
        "2nd",
        "3rd",
        "4th",
        "5th",
        "6th",
        "7th",
        "8th",
        "9th",
        "10th",
        "11th",
        "12th",
        "13th",
        "14th",
        "15th",
        "16th",
        "17th",
        "18th",
        "19th",
        "20th",
        "21st",
        "22nd",
        "23rd",
        "24th",
        "25th",
        "26th",
        "27th",
        "28th",
        "last day",
    ];

    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    return (
        <div className="bg-white p-6 shadow-md rounded-md">
            <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center">📅 Accrual Schedule</h2>
                {/* <button onClick={inspect}>inspect</button> */}
            </div>

            {/* Employee Accrual */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="text-gray-700">Employee accrue</span>
                <input
                    type="text"
                    className="w-20 px-3 py-2 border rounded-md appearance-none"
                    value={formData.accrual_rate ?? ""}
                    onChange={(e) => {
                        let value = e.target.value;

                        // Allow only numbers, decimals, and handle cases like "0."
                        if (/^\d*\.?\d*$/.test(value)) {
                            handleChange("accrual_rate", value === "" ? undefined : value);
                        }
                    }}
                    onBlur={() => {
                        if (
                            formData.accrual_rate === undefined ||
                            isNaN(Number(formData.accrual_rate)) ||
                            Number(formData.accrual_rate) < 0
                        ) {
                            handleChange("accrual_rate", 0); // Reset to 0 if invalid or negative
                        } else {
                            handleChange("accrual_rate", parseFloat(formData.accrual_rate.toString())); // Convert to number on blur
                        }
                    }}
                />
                <span className="text-gray-700">hours</span>
                <select
                    className="appearance-none w-48 px-3 py-1 border rounded-md"
                    value={formData.accrual_period}
                    onChange={(e) => handleChange("accrual_period", e.target.value)}
                >
                    {Object.values(TimeOffAccrualPeriodEnum)
                        .filter((it) => it !== TimeOffAccrualPeriodEnum.DAILY)
                        .map((period) => (
                            <option key={period} value={period}>
                                {TimeoffAccrualTitle[period]}
                            </option>
                        ))}
                </select>

                {formData.accrual_period === TimeOffAccrualPeriodEnum.WEEKLY && (
                    <>
                        <span className="text-gray-700">on</span>
                        <select
                            className="appearance-none w-48 px-3 py-1 border rounded-md"
                            value={formData.accrual_frequency_json?.[TimeOffAccrualPeriodEnum.WEEKLY]?.day_of_week}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,
                                    accrual_frequency_json: {
                                        ...prev.accrual_frequency_json,
                                        [TimeOffAccrualPeriodEnum.WEEKLY]: {
                                            day_of_week: e.target.value,
                                        },
                                    },
                                }));
                            }}
                        >
                            {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
                                (day, index) => (
                                    <option key={index} value={day}>
                                        {day}
                                    </option>
                                )
                            )}
                        </select>
                    </>
                )}

                {formData.accrual_period === TimeOffAccrualPeriodEnum.MONTHLY && (
                    <>
                        <span className="text-gray-700">on the</span>
                        <select
                            className="appearance-none w-flex px-3 py-1 border rounded-md"
                            value={formData.accrual_frequency_json?.[TimeOffAccrualPeriodEnum.MONTHLY]?.day_of_month}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,

                                    accrual_frequency_json: {
                                        ...prev.accrual_frequency_json,
                                        [TimeOffAccrualPeriodEnum.MONTHLY]: {
                                            day_of_month: Number(e.target.value),
                                        },
                                    },
                                }));
                            }}
                        >
                            {ordinalNumbers.map((label, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </>
                )}

                {formData.accrual_period === TimeOffAccrualPeriodEnum.YEARLY && (
                    <>
                        {/* date */}
                        <span className="text-gray-700">on the</span>
                        <select
                            className="appearance-none w-flex px-3 py-1 border rounded-md"
                            value={formData.accrual_frequency_json?.[TimeOffAccrualPeriodEnum.YEARLY]?.date_of_month}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,
                                    accrual_frequency_json: {
                                        ...prev.accrual_frequency_json,
                                        [TimeOffAccrualPeriodEnum.YEARLY]: {
                                            date_of_month: Number(e.target.value),
                                        },
                                    },
                                }));
                            }}
                        >
                            {ordinalNumbers.map((label, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {label}
                                </option>
                            ))}
                        </select>
                        {/* month */}
                        <span className="text-gray-700">of</span>
                        <select
                            className="appearance-none w-flex px-3 py-1 border rounded-md"
                            value={formData.accrual_frequency_json?.[TimeOffAccrualPeriodEnum.YEARLY]?.month_name}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,
                                    accrual_frequency_json: {
                                        ...prev.accrual_frequency_json,
                                        [TimeOffAccrualPeriodEnum.YEARLY]: {
                                            month_of_year: Number(e.target.value),
                                        },
                                    },
                                }));
                            }}
                        >
                            {months.map((label, i) => (
                                <option key={i + 1} value={label}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </>
                )}

                {formData.accrual_period == TimeOffAccrualPeriodEnum.ON_ANNIVERSARY && (
                    <>
                        <span>of their hire date.</span>
                    </>
                )}
            </div>

            <hr className="my-4" />

            {/* Accrual Options */}
            <h3 className="text-lg font-semibold">Accrual Options</h3>

            {/* Waiting Period */}
            <div className="mt-2">
                <label className="block font-semibold text-gray-700">Waiting Period</label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={!formData.has_waiting_period}
                            onChange={() => handleChange("has_waiting_period", false)}
                            className="mr-2"
                        />
                        No, they begin accruing time immediately on hire
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={formData.has_waiting_period}
                            onChange={() => handleChange("has_waiting_period", true)}
                            className="mr-2"
                        />
                        Yes, there is a waiting period
                    </label>

                    {formData.has_waiting_period && (
                        <div className="flex gap-4 items-center">
                            <span className="text-gray-700">of</span>
                            <input
                                type="text"
                                className="w-20 px-3 py-1 border rounded-md appearance-none"
                                value={formData.waiting_period_json?.[selectedWaitingPeriodType] ?? ""}
                                onChange={(e) => {
                                    let value = e.target.value;

                                    // Allow only numbers, decimals, and handle cases like "0."
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        setFormData((prev: any) => ({
                                            ...prev,
                                            waiting_period_json: {
                                                [selectedWaitingPeriodType]: value,
                                            },
                                        }));
                                    }
                                }}
                                onBlur={() => {
                                    const value = Number(formData.waiting_period_json?.[selectedWaitingPeriodType]);

                                    // Ensure valid number & prevent negatives
                                    setFormData((prev: any) => ({
                                        ...prev,
                                        waiting_period_json: {
                                            ...prev.waiting_period_json,
                                            [selectedWaitingPeriodType]: isNaN(value) || value < 0 ? 0 : value,
                                        },
                                    }));
                                }}
                            />
                            <select
                                className="appearance-none w-flex px-3 py-1 border rounded-md"
                                value={selectedWaitingPeriodType}
                                onChange={(e) => {
                                    const selectedEnum =
                                        TimeoffAccrualWaitingPeriod[
                                            e.target.value as keyof typeof TimeoffAccrualWaitingPeriod
                                        ];

                                    setSelectedWaitingPeriodType(selectedEnum);
                                    setFormData((prev) => ({
                                        ...prev,
                                        waiting_period_json: {
                                            [selectedEnum]: 0,
                                        },
                                    }));
                                }}
                            >
                                {Object.values(TimeoffAccrualWaitingPeriod).map((period) => (
                                    <option key={period} value={period}>
                                        {TimeoffAccrualWaitingPeriodTitle[period]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Accrual Cap */}
            <div className="mt-4">
                <label className="block font-semibold text-gray-700">Accrual Cap</label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={!formData.cap_enabled}
                            onChange={() => handleChange("cap_enabled", false)}
                            className="mr-2"
                        />
                        No, they will continue to accrue time, regardless of balance
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={formData.cap_enabled}
                            onChange={() => handleChange("cap_enabled", true)}
                            className="mr-2"
                        />
                        Yes, they stop accruing at a certain balance
                    </label>
                    {formData.cap_enabled && (
                        <div className="flex gap-4 items-center">
                            <span className="text-gray-700">of</span>
                            <input
                                type="text"
                                className="w-20 px-3 py-1 border rounded-md appearance-none"
                                value={formData.cap_hours_limit ?? ""}
                                onChange={(e) => {
                                    let value = e.target.value;

                                    // Allow only numbers, decimals, and handle cases like "0."
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        setFormData((prev: any) => ({
                                            ...prev,
                                            cap_hours_limit: value,
                                        }));
                                    }
                                }}
                                onBlur={() => {
                                    const value = Number(formData.cap_hours_limit);

                                    // Ensure valid number & prevent negatives
                                    setFormData((prev: any) => ({
                                        ...prev,
                                        cap_hours_limit: isNaN(value) || value < 0 ? 0 : value,
                                    }));
                                }}
                            />
                            <span className="text-gray-700">hours</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Carry Over */}
            <div className="mt-4">
                <label className="block font-semibold text-gray-700">Carry Over</label>
                <div className="flex gap-4">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={!formData.carryover_enabled}
                            onChange={() => handleChange("carryover_enabled", false)}
                            className="mr-2"
                        />
                        No, it’s 'use it or lose it'
                    </label>
                    <label className="flex items-center">
                        <input
                            type="radio"
                            checked={formData.carryover_enabled}
                            onChange={() => handleChange("carryover_enabled", true)}
                            className="mr-2"
                        />
                        Yes, unused time off can be carried over
                    </label>
                    {formData.carryover_enabled && (
                        <div className="flex gap-4 items-center">
                            <select
                                className="appearance-none w-flex px-3 py-1 border rounded-md"
                                value={formData.carryover_json?.rule}
                                onChange={(e) => {
                                    const selectedEnum =
                                        TimeoffCarryOverRule[e.target.value as keyof typeof TimeoffCarryOverRule];
                                    setFormData((prev) => ({
                                        ...prev,
                                        carryover_json: {
                                            rule: selectedEnum,
                                        },
                                    }));
                                }}
                            >
                                {Object.values(TimeoffCarryOverRule).map((rule) => (
                                    <option key={rule} value={rule}>
                                        {TimeoffCarryOverRuleTitle[rule]}
                                    </option>
                                ))}
                            </select>
                            {formData.carryover_json?.rule == TimeoffCarryOverRule.UPTO && (
                                <input
                                    type="text"
                                    className="w-20 px-3 py-1 border rounded-md appearance-none"
                                    value={formData.carryover_json?.hours ?? ""}
                                    onChange={(e) => {
                                        let value = e.target.value;

                                        // Allow only numbers, decimals, and handle cases like "0."
                                        if (/^\d*\.?\d*$/.test(value)) {
                                            setFormData((prev: any) => ({
                                                ...prev,
                                                carryover_json: {
                                                    ...prev.carryover_json,
                                                    hours: value,
                                                },
                                            }));
                                        }
                                    }}
                                    onBlur={() => {
                                        const value = Number(formData.carryover_json?.hours);

                                        // Ensure valid number & prevent negatives
                                        setFormData((prev: any) => ({
                                            ...prev,
                                            carryover_json: {
                                                ...prev.carryover_json,
                                                hours: isNaN(value) || value < 0 ? 0 : value,
                                            },
                                        }));
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Milestone Forms */}
            <div className="flex flex-col mt-4 gap-y-4">
                {milestones.map((milestone) => (
                    <MilestoneForm
                        onRemove={removeMilestone}
                        onSave={updateMilestone}
                        key={milestone.order}
                        milestone={milestone}
                    />
                ))}
            </div>

            <div className="flex justify-start mt-4">
                <div
                    className="inline-flex items-center px-3 py-2 text-md text-blue-500 font-bold rounded-md cursor-pointer"
                    onClick={addMilestone}
                >
                    <PlusCircle className="mr-2 -ml-1 h-5 w-5" aria-hidden="true" />
                    Add Milestone
                </div>
            </div>

            <hr className="my-6" />

            {/* Carry Over Rules */}
            <h3 className="text-lg font-semibold">Carryover Rules</h3>
            <div className="flex gap-4">
                {/* Dropdown */}
                <select
                    className={`px-3 py-2 border rounded-md ${
                        formData.carryover_json?.carryover_date === "Other" ? "w-1/2" : "w-full"
                    }`}
                    value={carryOverRulesSelectedvalue}
                    onChange={(e) => {
                        let selectedValue = e.target.value;
                        setCarryOverRulesSelectedvalue(selectedValue);
                        if (selectedValue === "1st of January") {
                            setShowOtherDatePicker(false);
                            selectedValue = DateTimeUtil.formatDateToMySQL(
                                new Date(new Date().getFullYear(), 0, 1)
                            ).split(" ")[0]; // Store as date string
                        } else if (selectedValue === "Employee hire date") {
                            setShowOtherDatePicker(false);
                            selectedValue = user.osEmpHireDate || user.os_emp_hire_date || "";
                        } else if (selectedValue === "Other") {
                            setShowOtherDatePicker(true);
                            selectedValue = ""; // Reset for custom date selection
                        }

                        setFormData((prev) => ({
                            ...prev,
                            carryover_json: {
                                ...prev.carryover_json,
                                carryover_date: selectedValue,
                            },
                        }));
                    }}
                >
                    <option value={"1st of January"}>1st of January</option>
                    <option value={"Employee hire date"}>Employee hire date</option>
                    <option value={"Other"}>Others...</option>
                </select>

                {/* Show Date Input if "Other" is selected */}
                {showOtherDatePicker && (
                    <input
                        type="date"
                        className="w-1/2 px-3 py-2 border rounded-md cursor-pointer"
                        value={
                            formData.carryover_json?.carryover_date
                                ? new Date(formData.carryover_json.carryover_date).toISOString().split("T")[0]
                                : ""
                        }
                        onChange={(e) => {
                            const dateString = new Date(e.target.value).toISOString();
                            setFormData((prev) => ({
                                ...prev,
                                carryover_json: {
                                    ...prev.carryover_json,
                                    carryover_date: dateString,
                                },
                            }));
                        }}
                        onClick={(e) => e.currentTarget.showPicker()} // Ensures clicking anywhere opens the datepicker
                    />
                )}
            </div>

            {/* Checkbox for Reset Negative Balance */}
            <div className="mt-4">
                <label className="flex items-center">
                    <input
                        type="checkbox"
                        checked={formData.carryover_json?.reset_negative_balance}
                        onChange={(e) => {
                            setFormData((prev) => ({
                                ...prev,
                                carryover_json: {
                                    ...prev.carryover_json,
                                    reset_negative_balance: e.target.checked,
                                },
                            }));
                        }}
                        className="mr-2"
                    />
                    Reset negative balances to 0 hours on carryover date
                </label>
            </div>

            <hr className="my-6" />

            {/* Fine-Tuning */}
            <h3 className="text-lg font-semibold">Fine-tuning</h3>
            <label className="block text-gray-700 mt-2">Employees’ first accrual should be...</label>
            <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.fine_tuning_json?.prorating}
                onChange={(e) => handleChange("firstAccrual", e.target.value)}
            >
                <option value="Prorated">Prorated, based on the accrual period</option>
                <option value="Full">The full amount of the accrual period</option>
            </select>

            <label className="block text-gray-700 mt-2">Employees receive accrued time...</label>
            <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.fine_tuning_json?.timing}
                onChange={(e) => handleChange("accrualReceipt", e.target.value)}
            >
                <option value="End of period">At the end of accrual period</option>
                <option value="Start of period">At the start of accrual period</option>
            </select>
        </div>
    );
};

export default AccrualSetupStep;
